import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import Terms from './components/Terms';
import './AuthForm.css';

const AuthForm = () => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const registrationDisabled = true; // Registration is disabled - contact Jack for access
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showTerms, setShowTerms] = useState(false);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password strength validation
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push(`At least ${minLength} characters`);
    }
    if (!hasUpperCase) {
      errors.push('One uppercase letter');
    }
    if (!hasLowerCase) {
      errors.push('One lowercase letter');
    }
    if (!hasNumbers) {
      errors.push('One number');
    }
    if (!hasSpecialChar) {
      errors.push('One special character');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  // Client-side validation
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isLogin) {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = `Password must have: ${passwordValidation.errors.join(', ')}`;
      }
    }

    // Confirm password validation (registration only)
    if (!isLogin) {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general error
    if (generalError) {
      setGeneralError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Block registration attempts
    if (!isLogin && registrationDisabled) {
      setGeneralError('Registration is currently disabled. Please contact Jack for account access.');
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setGeneralError('');

    try {
      // Normalize email to lowercase before sending to backend
      const normalizedEmail = formData.email.toLowerCase().trim();
      
      const result = isLogin 
        ? await login(normalizedEmail, formData.password)
        : await register(normalizedEmail, formData.password);

      if (!result.success) {
        setGeneralError(result.error);
      }
      // If successful, the AuthContext will handle the state update and redirect
    } catch (error) {
      console.error(`${isLogin ? 'Login' : 'Registration'} error:`, error);
      setGeneralError(`${isLogin ? 'Login' : 'Registration'} failed. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };



  const switchMode = () => {
    // Prevent switching to registration mode if disabled
    if (isLogin && registrationDisabled) {
      setGeneralError('Registration is currently disabled. Please contact Jack for account access.');
      return;
    }
    
    setIsLogin(!isLogin);
    setFormData({
      email: '',
      password: '',
      confirmPassword: ''
    });
    setErrors({});
    setGeneralError('');
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1>🎓 Practicum</h1>
        <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
        <p>
          {isLogin 
            ? 'Sign in to access your AI assistant' 
            : registrationDisabled 
              ? 'Registration is currently disabled. Please contact Jack for account access.'
              : 'Join Practicum to start your AI-powered learning journey'
          }
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Email address"
              disabled={isLoading}
              className={errors.email ? 'error' : ''}
              autoFocus
              autoComplete="email"
              inputMode="email"
              spellCheck="false"
            />
            {errors.email && (
              <div className="field-error">{errors.email}</div>
            )}
          </div>

          <div className="form-group">
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Password"
              disabled={isLoading}
              className={errors.password ? 'error' : ''}
              autoComplete={isLogin ? "current-password" : "new-password"}
            />
            {errors.password && (
              <div className="field-error">{errors.password}</div>
            )}
          </div>

          {!isLogin && (
            <div className="form-group">
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Confirm password"
                disabled={isLoading}
                className={errors.confirmPassword ? 'error' : ''}
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <div className="field-error">{errors.confirmPassword}</div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !formData.email || !formData.password || (!isLogin && !formData.confirmPassword) || (!isLogin && registrationDisabled)}
            className="auth-button"
          >
            {isLoading 
              ? (isLogin ? 'Signing in...' : 'Creating account...') 
              : (isLogin ? 'Sign In' : registrationDisabled ? 'Registration Disabled' : 'Create Account')
            }
          </button>
        </form>

        {generalError && (
          <div className="error-message">
            {generalError}
          </div>
        )}

        <div className="auth-switch">
          <p>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            {registrationDisabled && isLogin ? (
              <span style={{ color: '#666', fontStyle: 'italic' }}>
                Contact Jack for access
              </span>
            ) : (
              <button 
                type="button" 
                onClick={switchMode}
                className="switch-mode-button"
                disabled={isLoading || (isLogin && registrationDisabled)}
              >
                {isLogin ? 'Create one' : 'Sign in'}
              </button>
            )}
          </p>
        </div>

        <div className="terms-notice">
          <p>
            By using this service, you agree to our{' '}
            <button 
              type="button" 
              onClick={() => setShowTerms(true)}
              className="terms-link"
            >
              Terms of Service
            </button>
          </p>
        </div>
      </div>

      {showTerms && (
        <Terms onClose={() => setShowTerms(false)} />
      )}
    </div>
  );
};

export default AuthForm;